import React, { useState, useEffect } from 'react'

import {
    StyleSheet,
    View,
    Animated,
    RefreshControl,
    ScrollView,
    Platform,
    Text,
    Dimensions
} from 'react-native'

import { useSafeAreaInsets } from 'react-native-safe-area-context'

import moment from 'moment'

import { useContent } from '@sart/content'

import client, { useClient } from '@sart/client'
import {
    adaptive,
    colors,
    backgroundColors,
    typography,
    radius,
    shadows
} from '@sart/styles'
import {
    Space,
    ProductSubscription,
    ProductPayPerView,
} from '@sart/components'
import {
    Logo, CompanyIcon, Mobile
} from '@sart/icons'
// import { TV } from '@sart/icons'


const BRANDING_HEIGHT = 24
const MAX_DIALS_HEIGHT = 120
const MIN_DIALS_HEIGHT = 48
const EXPIRATION_HEIGHT = 24

export default function MediaDashboard(props) {
    const { account, service, navigation, reloadData } = props
    const content = useContent()
    const [products, setProducts] = useState({})
    const [latestInvoice, setLatestInvoice] = useState(false)
    const [ balances, setBalances ] = useState(undefined)

    const {
        cache: {
            language,
            customer,
        } = {}
    } = useClient()
    const insets = useSafeAreaInsets()

    useEffect(() => {
        loadProductData()
        loadBalanceData()
        loadCustomerData()
    }, [])

    async function loadProductData() {
        try {
            let productData = await client.getMediaProducts(account, service)
            setProducts(productData)
        } catch (error) {
            console.log(error)
        }
    }

    async function loadBalanceData() {
        let balanceData = await client.getAccountBalances(account, service)
        setLatestInvoice(balanceData.latestInvoice)
        client.setCache({
          latestInvoice: balanceData.latestInvoice
        })
        setBalances(balanceData)
      }

    function loadCustomerData() {
        //If the customer has not been loaded yet, load it
        //Mainly here for web app refresh issue SRPRODDEV-386
        if (customer === undefined) {
            client.loadPersonalDetails()
        }
    }

    const MAX_HEADER_HEIGHT = BRANDING_HEIGHT + MAX_DIALS_HEIGHT + EXPIRATION_HEIGHT
    const MIN_HEADER_HEIGHT = MIN_DIALS_HEIGHT
    const HEADER_HEIGHT_DELTA = MAX_HEADER_HEIGHT - MIN_HEADER_HEIGHT

    const scrollY = new Animated.Value(0)

    const headerY = scrollY.interpolate({
        inputRange: [0, HEADER_HEIGHT_DELTA],
        outputRange: [0, 1],
        extrapolateRight: 'clamp' // Animated.Extrapolate.CLAMP
    })

    const translateY = headerY.interpolate({
        inputRange: [0, 1],
        outputRange: [0, 36],
        extrapolateLeft: 'clamp' // Animated.Extrapolate.CLAMP
    })

    const headerHeight = headerY.interpolate({
        inputRange: [0, 1],
        outputRange: [MAX_HEADER_HEIGHT, MIN_HEADER_HEIGHT],
        extrapolateLeft: 'clamp' // Animated.Extrapolate.CLAMP
    })

    const spacerHeight = headerY.interpolate({
        inputRange: [0, 1],
        outputRange: [0, HEADER_HEIGHT_DELTA],
    })

    const hide = headerY.interpolate({
        inputRange: [0, 0.7],
        outputRange: [1, 0],
    })

    const show = headerY.interpolate({
        inputRange: [0.7, 1],
        outputRange: [0, 1],
    })

    return (
        <View>
            <Animated.ScrollView
                scrollEventThrottle={1}
                // stickyHeaderIndices={[0,]}
                showsVerticalScrollIndicator={true}
                style={styles.container}
                onScroll={
                    Animated.event([
                        { nativeEvent: { contentOffset: { y: scrollY } } }
                    ],{})
                }
            >
                <View style={styles.headerContainer}>
                    <View style={styles.rowContainer}>
                        <View style={{ justifyContent: 'flex-end' }}>
                            <CompanyIcon color={colors.srGreen} height={60} width={60} />
                        </View>
                        {latestInvoice && 
                        <View style={styles.amountDue}>
                            <Space height={12} />
                            <Text style={styles.amountDueTitle}>{content.dashboard.amountDue}</Text>
                            <Text style={styles.amountDueText}>{content.common.currency}{(latestInvoice.outstandingAmountDue / 100).toFixed(2)}</Text>
                        </View>

                        }
                        {!latestInvoice &&
                        <View style={styles.amountDue}>
                            <Space height={12} />
                            <Text style={styles.amountDueTitle}>{content.dashboard.invoiceLoading}</Text>
                        </View>
                        }
                        
                    </View>

                    <Animated.View style={styles.headerMain}>
                        <View>
                            <View>
                                <Space height={12} />
                                <View style={styles.numberContainer}>
                                    {/* <TV color={colors.srTextInput} size={28} /> */}
                                    <Space width={10} />
                                    <Text style={styles.numberText}>{service.lineIdentifier}</Text>
                                </View>
                                <Space height={12} />
                            </View>
                        </View>
                    </Animated.View>
                </View>

                <View style={styles.content}>
                    <View style={styles.subscriptions}>
                        <Text style={styles.title}>{content.dashboard.activeSubscriptions}</Text>
                        <View style={styles.subscriptionList}>
                            {products.mediaSubscriptions && products.mediaSubscriptions.map((addOn, i) =>
                                <React.Fragment key={i}>
                                    <ProductSubscription
                                        iconColor={colors.white}
                                        style={styles.addonStyle}
                                        placeholderStyle={styles.balanceItem}
                                        disabled={true}
                                        product={{
                                            ...addOn.product,
                                            expiration: addOn.expiryDateTime
                                        }}
																				language={language}
																				injectedContent={content}
                                    />
                                    <Space height={24} />
                                </React.Fragment>
                            )}
                        </View>
                    </View>
										{ products.mediaPPVs && products.mediaPPVs.length > 0 &&
                    <View style={styles.ppvs}>
                        <Text style={styles.title}>{content.dashboard.payPerViews}</Text>
                        <View style={styles.ppvList}>
                        {products.mediaPPVs && products.mediaPPVs.map((addOn, i) =>
                                <React.Fragment key={i}>
                                    <ProductPayPerView
                                        iconColor={colors.white}
                                        style={styles.addonStyle}
                                        placeholderStyle={styles.balanceItem}
                                        disabled={true}
                                        product={{
                                            ...addOn.product,
                                            expiration: addOn.expiryDateTime
                                        }}
																				language={language}
																				injectedContent={content}
                                    />
                                    <Space height={24} />
                                </React.Fragment>
                            )}
                        </View>
                    </View>
										}
                </View>
            </Animated.ScrollView>
        </View>

    )
}

const styles = StyleSheet.create({
    container: {
        // flex: 1,
        width: Dimensions.get('window').width,
    },
    headerContainer: {
        justifyContent: 'center',
        ...adaptive.wrapperPadding,
        backgroundColor: colors.white,
        borderColor: colors.white,
        // flex: 1,
        paddingTop: Platform.OS === 'ios' ? 30 : null
    },
    branding: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    line: {
        borderColor: colors.subtle,
        borderWidth: StyleSheet.hairlineWidth,
    },
    content: {
        paddingTop: 12,
        ...adaptive.wrapperPadding,
        paddingBottom: 24,
        height: '100%',
    },
    numberText: {
        textAlign: 'center',
        ...typography.bold,
        fontSize: 24,
        color: colors.srTextInput
    },
    headerMain: {
        flexDirection: 'row',
        justifyContent: 'space-evenly',
    },
    amountDue: {
    },
    amountDueTitle: {
        textAlign: 'right',
        ...typography.bold,
        fontSize: 16,
        color: colors.srDarkGreen
    },
    amountDueText: {
        textAlign: 'right',
        ...typography.bold,
        fontSize: 20,
        color: colors.srDarkGreen
    },
    rowContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: '100%'
    },
    numberContainer: {
        flexDirection: 'row',
        justifyContent: 'center'
    },
    subscriptions: {

    },
    ppvs: {

    },
    title: {
        color: colors.srDarkGreen,
        fontSize: 20,
        ...typography.bold,
        padding: 10
    },
    balanceItem: {
        backgroundColor: colors.srGreen
      },
})

